import React, { useEffect, useState } from 'react';
import { apiRequest } from "../utils/api";
import { resolveSetJansForItems } from "../utils/smartDetaxSetJans";
import { ENABLE_SMARTDETAX_LEGACY_FEATURES } from "../utils/smartDetaxQr";
import { useParams, useNavigate } from 'react-router-dom';
import { useAlert } from '../components/AlertModal';
import { printReceipt, printSmartDetaxReceipt } from '../components/ReceiptPrinter';
import '../styles/TransactionHistoryDetail.css';

const TransactionHistoryDetail = () => {
    const { receiptId } = useParams();
    const [receipt, setReceipt] = useState(null);
    const [isEditingReceipt, setIsEditingReceipt] = useState(false);
    const [editedReceipt, setEditedReceipt] = useState(null);
    const [editingDetailId, setEditingDetailId] = useState(null);
    const [editedDetail, setEditedDetail] = useState(null);
    const [taxRate, setTaxRate] = useState(10);
    const [smartDetaxPrinting, setSmartDetaxPrinting] = useState(false);
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    useEffect(() => {
        const apiReceiptDetails = async () => {
            const response = await apiRequest(
                `inventory/sales_receipts/${receiptId}/details/`,
                'GET'
            );

            setReceipt(response);
            setEditedReceipt(response.receipt);
        };

        const fetchTaxRate = async () => {
            try {
                const rates = await apiRequest('inventory/tax_rates/', 'GET');

                // 税率APIは配列・単一オブジェクトの両方に対応
                const rateValue = Array.isArray(rates)
                    ? rates[0]?.tax_rate
                    : rates?.tax_rate;
                const parsedRate = Number(rateValue);
                if (Number.isFinite(parsedRate) && parsedRate > 0) {
                    setTaxRate(parsedRate);
                }
            } catch (e) {
                console.error("税率取得失敗", e);
            }
        };

        apiReceiptDetails();
        fetchTaxRate();
    }, [receiptId]);

    const handleSaveReceipt = async () => {
        const updatedPayload = {
            ...editedReceipt,
            register_id: receipt.receipt.register.id,
        };
        try {
            const updated = await apiRequest(
                `inventory/sales_receipts/${receipt.receipt.id}/`,
                'PATCH',
                updatedPayload
            );
            setReceipt((prev) => ({ ...prev, receipt: updated }));
            setEditedReceipt(updated);
            setIsEditingReceipt(false);
        } catch {
            showAlert('保存に失敗しました');
        }
    };

    const handleEditDetail = (detail) => {
        setEditingDetailId(detail.id);
        setEditedDetail({ ...detail });
    };

    const handleSaveDetail = async () => {
        try {
            const updated = await apiRequest(
                `inventory/sales_details/${editedDetail.id}/`,
                'PATCH',
                {
                    quantity: editedDetail.quantity,
                    unit_price: editedDetail.unit_price,
                    discount: editedDetail.discount,
                    subtotal: editedDetail.subtotal,
                }
            );
            const newDetails = receipt.details.map((d) =>
                d.id === updated.id ? { ...updated } : d
            );
            setReceipt((prev) => ({ ...prev, details: newDetails }));
            setEditingDetailId(null);
            setEditedDetail(null);
        } catch {
            showAlert('販売明細の保存に失敗しました');
        }
    };

    const handleReprintReceipt = () => {
        const subtotal = Number(receiptInfo.total_amount || 0);
        const savedTaxAmount = Number(receiptInfo.tax_amount || 0);

        const isTaxFree =
            receiptInfo.sale_type === "tax_exempt";

        // 過去伝票に保存された方式を使用
        // 古いデータで値が無い場合は従来方式として扱う
        const taxFreeMode =
            receiptInfo.tax_free_mode || "legacy";

        // 従来免税は保存税額が0なので、本来の税額を再計算
        const taxBeforeExemption =
            isTaxFree && taxFreeMode === "legacy"
                ? Math.floor(subtotal * (taxRate / 100))
                : savedTaxAmount;

        // 実際に伝票へ保存されている税額
        const taxAmount = savedTaxAmount;

        // 本来の税込金額
        const totalWithTax =
            subtotal + taxBeforeExemption;

        // 実際の支払額
        const total =
            isTaxFree && taxFreeMode === "legacy"
                ? subtotal
                : subtotal + taxAmount;

        const receiptData = {
            id: `${receiptInfo.id}R`,
            registerId: receiptInfo.register.id,
            date: receiptInfo.date,

            items: details.map((detail) => ({
                name: detail.product_name,
                quantity: Number(detail.quantity || 0),
                price: Number(detail.unit_price || 0),
                discount: Number(detail.discount || 0),

                jan:
                    (!detail.barcode || Number(detail.barcode) === 0)
                        ? (
                            !detail.jan || Number(detail.jan) === 0
                                ? ""
                                : detail.jan
                        )
                        : detail.barcode,

                set_sale: Number(detail.set_sale || 0),
            })),

            totalItems: Number(
                receiptInfo.total_quantity || 0
            ),

            subtotal: subtotal,

            // 実際に徴収した税
            tax: taxAmount,

            // 免税しなかった場合の本来の税
            taxBeforeExemption: taxBeforeExemption,

            taxFreeAmount: Number(
                receiptInfo.tax_exemption || 0
            ),

            isTaxFree: isTaxFree,

            // ★この伝票に保存されている免税方式
            taxFreeMode: taxFreeMode,

            totalWithTax: totalWithTax,

            total: total,

            paidCash: Number(
                receiptInfo.cash_received_amount || 0
            ),

            paidCashless: Number(
                receiptInfo.cashless_payment_amount || 0
            ),

            change: Number(
                receiptInfo.changeinfo || 0
            ),
        };

        printReceipt(receiptData, showAlert);
    };


    // 取引履歴からSmartDetax専用QR伝票を必要時だけ印刷する。
    // 通常レシートの再印刷とは別処理。売上を再登録したり、購入者用レシートは印刷しない。
    const handlePrintSmartDetax = async () => {
        if (smartDetaxPrinting || !receipt?.receipt) return;

        setSmartDetaxPrinting(true);
        try {
            const info = receipt.receipt;
            if (info.sale_type !== "tax_exempt") {
                throw new Error("免税販売の伝票のみ印刷できます");
            }

            // 通常の再印刷で付けている末尾「R」はQRレシート番号には付けない。
            const originalReceiptNo = String(info.id ?? "").trim();
            if (!/^\d{1,10}$/.test(originalReceiptNo)) {
                throw new Error("元のレシート番号を取得できません");
            }

            const registerId = info.register?.id ?? info.register_id;
            if (registerId == null || registerId === "") {
                throw new Error("販売時のレジ番号を取得できません");
            }

            // 販売した「当時の店舗」を使用する。今ログインしている店舗で代用しない。
            const getId = (value) =>
                value && typeof value === "object" ? value.id ?? null : value;

            let storeId = getId(
                info.store_id ?? info.register?.store_id ?? info.register?.store
            );

            if (storeId == null || storeId === "") {
                // レシート詳細APIに店舗IDが無い場合、伝票に紐づくレジから取得。
                const register = await apiRequest(
                    `inventory/registers/${registerId}/`, "GET"
                );
                storeId = getId(register?.store_id ?? register?.store);
            }
            if (storeId == null || storeId === "") {
                throw new Error("販売店舗IDを取得できません。レジの店舗設定を確認してください");
            }

            const qrReceiptData = {
                id: originalReceiptNo,
                storeId,
                registerId,
                date: info.date,
                isTaxFree: true,
                taxFreeMode: info.tax_free_mode === "refund" ? "refund" : "legacy",
                items: (receipt.details || []).map(detail => ({
                    inventoryId: detail.inventory_fk_id ?? detail.inventory?.id ??
                        (typeof detail.inventory === "number" ? detail.inventory : null),
                    name: detail.product_name,
                    quantity: Number(detail.quantity),
                    price: Number(detail.unit_price),
                    // 値引き反映済みの明細金額を使用。欠損値はQR生成側でエラーにする。
                    subtotal: detail.subtotal == null ? undefined : Number(detail.subtotal),
                    discount: Number(detail.discount || 0),
                    jan: String(detail.barcode ?? "") !== "0" && detail.barcode
                        ? String(detail.barcode)
                        : String(detail.jan ?? "").replace(/^0$/, ""),
                    set_sale: Number(detail.set_sale || 0),
                    set_items: detail.set_items ?? detail.set_ids ?? "",
                    taxRate: Number(detail.tax_rate ?? detail.taxRate ?? taxRate),
                    goodsType: detail.goods_type ?? detail.goodsType ?? 1,
                    reducedTax: detail.reduced_tax ?? detail.reducedTax,
                    specialPackaging: detail.special_packaging ?? detail.specialPackaging,
                    categoryCode: detail.category_code ?? detail.categoryCode,
                    featureInfo: detail.feature_info ?? detail.featureInfo,
                    serialNumber: detail.serial_number ?? detail.serialNumber,
                    isTaxFreeEligible: detail.is_tax_free_eligible ?? detail.isTaxFreeEligible,
                })),
            };

            // 取引履歴でも従来/リファンド方式のSET構成品コードを取得する。
            // 売上データは変更しない。
            if (qrReceiptData.taxFreeMode === "refund" || ENABLE_SMARTDETAX_LEGACY_FEATURES) {
                qrReceiptData.items = await resolveSetJansForItems(
                    qrReceiptData.items,
                    id => apiRequest(`inventory/inventory/${id}/`, "GET")
                );
            }

            // ReceiptPrinter内で650byte/QR・最大8明細・Q1～Q10分割などを実行。
            await printSmartDetaxReceipt(qrReceiptData, showAlert);
        } catch (error) {
            console.error("[TransactionHistoryDetail] SmartDetax印刷エラー:", error);
            showAlert(`SmartDetax印刷に失敗しました：${error.message}`);
        } finally {
            setSmartDetaxPrinting(false);
        }
    };

    if (!receipt || !editedReceipt) return <div className="TransactionHistoryDetail-loading">Loading...</div>;

    const { receipt: receiptInfo, details, return_transactions } = receipt;

    return (
        <div className="TransactionHistoryDetail-container">
            <button className="TransactionHistoryDetail-backButton" onClick={() => navigate(-1)}>◀◀ 戻る</button>
            <div className="TransactionHistoryDetail-titleRow">
                <h2 className="TransactionHistoryDetail-title">
                    取引履歴詳細
                </h2>

                <button
                    className="TransactionHistoryDetail-reprintButton"
                    onClick={handleReprintReceipt}
                >
                    レシート再印刷
                </button>

                {receiptInfo.sale_type === "tax_exempt" && (
                    <button
                        type="button"
                        className="TransactionHistoryDetail-reprintButton"
                        style={{ marginLeft: "8px" }}
                        onClick={handlePrintSmartDetax}
                        disabled={smartDetaxPrinting}
                    >
                        {smartDetaxPrinting ? "印刷中..." : "SmartDetax印刷"}
                    </button>
                )}
            </div>
            

            <div className="TransactionHistoryDetail-section">
                <h3 className="TransactionHistoryDetail-subtitle">
                    販売伝票情報 (レジID：{receiptInfo.register.id})
                    {receiptInfo.is_edited && (
                        <span className="TransactionHistoryDetail-edited">
                            （修正済: {new Date(receiptInfo.last_edited_at).toLocaleString()}）
                        </span>
                    )}
                </h3>

                {isEditingReceipt ? (
                    <>
                        <label className="TransactionHistoryDetail-label">販売形態:</label>
                        <select
                            className="TransactionHistoryDetail-select"
                            value={editedReceipt.sale_type}
                            onChange={(e) => setEditedReceipt({ ...editedReceipt, sale_type: e.target.value })}
                        >
                            <option value="normal">通常販売</option>
                            <option value="tax_exempt">免税販売</option>
                        </select>
                        <label className="TransactionHistoryDetail-label">決済方法:</label>
                        <select
                            className="TransactionHistoryDetail-select"
                            value={editedReceipt.payment_method}
                            onChange={(e) => setEditedReceipt({ ...editedReceipt, payment_method: e.target.value })}
                        >
                            <option value="現金">現金</option>
                            <option value="キャッシュレス">キャッシュレス</option>
                        </select>

                        <label className="TransactionHistoryDetail-label">合計金額:</label>
                        <input
                            className="TransactionHistoryDetail-input"
                            type="number"
                            value={editedReceipt.total_amount}
                            onChange={(e) => setEditedReceipt({ ...editedReceipt, total_amount: e.target.value })}
                        />

                        <label className="TransactionHistoryDetail-label">値引き:</label>
                        <input
                            className="TransactionHistoryDetail-input"
                            type="number"
                            value={editedReceipt.discount}
                            onChange={(e) => setEditedReceipt({ ...editedReceipt, discount: e.target.value })}
                        />

                        <label className="TransactionHistoryDetail-label">
                            {editedReceipt.sale_type === "tax_exempt" &&
                            editedReceipt.tax_free_mode === "refund"
                                ? "仮受消費税:"
                                : "税額:"}
                        </label>
                        <input
                            className="TransactionHistoryDetail-input"
                            type="number"
                            value={editedReceipt.tax_amount}
                            onChange={(e) => setEditedReceipt({ ...editedReceipt, tax_amount: e.target.value })}
                        />

                        <label className="TransactionHistoryDetail-label">現金:</label>
                        <input
                            className="TransactionHistoryDetail-input"
                            type="number"
                            value={editedReceipt.cash_received_amount}
                            onChange={(e) => setEditedReceipt({ ...editedReceipt, cash_received_amount: e.target.value })}
                        />

                        <label className="TransactionHistoryDetail-label">キャッシュレス:</label>
                        <input
                            className="TransactionHistoryDetail-input"
                            type="number"
                            value={editedReceipt.cashless_payment_amount}
                            onChange={(e) => setEditedReceipt({ ...editedReceipt, cashless_payment_amount: e.target.value })}
                        />
                        <label className="TransactionHistoryDetail-label">現金預り金額:</label>
                        <input
                            className="TransactionHistoryDetail-input"
                            type="number"
                            value={
                                Number(editedReceipt.cash_received_amount || 0) +
                                Number(editedReceipt.changeinfo || 0)
                            }
                            readOnly
                        />
                        <label className="TransactionHistoryDetail-label">おつり:</label>
                        <input
                            className="TransactionHistoryDetail-input"
                            type="number"
                            value={editedReceipt.changeinfo ?? 0}
                            onChange={(e) =>
                                setEditedReceipt({
                                    ...editedReceipt,
                                    changeinfo: e.target.value,
                                })
                            }
                        />

                        <div className="TransactionHistoryDetail-buttonGroup">
                            <button onClick={handleSaveReceipt}>保存</button>
                            <button onClick={() => setIsEditingReceipt(false)}>キャンセル</button>
                        </div>
                    </>
                ) : (
                    <>
                        <p><strong>販売日:</strong> {new Date(receiptInfo.date).toLocaleString()}</p>
                        <p>
                            <strong>販売形態:</strong>{" "}
                            {receiptInfo?.sale_type === "normal"
                                ? "通常販売"
                                : receiptInfo?.sale_type === "tax_exempt" &&
                                receiptInfo?.tax_free_mode === "refund"
                                ? "免税販売（リファンド方式）"
                                : receiptInfo?.sale_type === "tax_exempt"
                                ? "免税販売（従来方式）"
                                : "不明"}
                        </p>
                        <p><strong>決済方法:</strong> {receiptInfo.payment_method}</p>
                        <p><strong>合計金額:</strong> ¥{receiptInfo.total_amount}</p>
                        <p><strong>値引き:</strong> ¥{receiptInfo.discount}</p>
                        <p>
                            <strong>
                                {receiptInfo.sale_type === "tax_exempt" &&
                                receiptInfo.tax_free_mode === "refund"
                                    ? "仮受消費税"
                                    : "税額"}:
                            </strong>{" "}
                            ¥{Number(receiptInfo.tax_amount || 0).toLocaleString()}
                        </p>
                        <p><strong>現金:</strong> ¥{receiptInfo.cash_received_amount}</p>
                        <p><strong>キャッシュレス:</strong> ¥{receiptInfo.cashless_payment_amount}</p>
                        <p>
                            <strong>現金を預かった金額:</strong> ¥
                            {(
                                Number(receiptInfo.cash_received_amount || 0) +
                                Number(receiptInfo.changeinfo || 0)
                            ).toLocaleString()}
                        </p>
                        <p><strong>おつり:</strong> ¥{receiptInfo.changeinfo}</p>
                        <button className='receiptInfo_editbutton' onClick={() => setIsEditingReceipt(true)}>編集</button>
                    </>
                )}
            </div>

            <div className="TransactionHistoryDetail-section">
                <h3 className="TransactionHistoryDetail-subtitle">販売明細</h3>
                <ul className="TransactionHistoryDetail-list">
                    {details.map((detail) => (
                        <li key={detail.id} className="TransactionHistoryDetail-item">
                            <strong>{detail.product_name}</strong>
                            {detail.is_edited && (
                                <span className="TransactionHistoryDetail-edited">
                                    （修正済: {new Date(detail.last_edited_at).toLocaleString()}）
                                </span>
                            )}
                            <br />
                            {editingDetailId === detail.id ? (
                                <>
                                    数量: <input type="number" value={editedDetail.quantity} onChange={(e) => setEditedDetail({ ...editedDetail, quantity: e.target.value })} />
                                    単価: <input type="number" value={editedDetail.unit_price} onChange={(e) => setEditedDetail({ ...editedDetail, unit_price: e.target.value })} />
                                    値引: <input type="number" value={editedDetail.discount} onChange={(e) => setEditedDetail({ ...editedDetail, discount: e.target.value })} />
                                    小計: <input type="number" value={editedDetail.subtotal} onChange={(e) => setEditedDetail({ ...editedDetail, subtotal: e.target.value })} />
                                    <div className="TransactionHistoryDetail-buttonGroup">
                                        <button onClick={handleSaveDetail}>保存</button>
                                        <button onClick={() => setEditingDetailId(null)}>キャンセル</button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    数量: {detail.quantity} / 単価: ¥{detail.unit_price} / 値引: ¥{detail.discount} / 小計: ¥{detail.subtotal}
                                    {detail.set_sale > 0 && (
                                        <>
                                            <br />
                                            セット構成: {detail.set_items}
                                        </>
                                    )}
                                    <br />
                                    <button onClick={() => handleEditDetail(detail)}>編集</button>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            {return_transactions.length > 0 && (
                <div className="TransactionHistoryDetail-section">
                    <h3 className="TransactionHistoryDetail-subtitle">返品履歴</h3>
                    <ul className="TransactionHistoryDetail-list">
                        {return_transactions.map((rt) => (
                            <li key={rt.id} className="TransactionHistoryDetail-item">
                                <p>返金方法: {rt.refund_method}</p>
                                <p>返品理由: {rt.reason}</p>
                                <p>返品日時: {new Date(rt.return_date).toLocaleString()}</p>
                                <ul className="TransactionHistoryDetail-sublist">
                                    {rt.details?.map((item, index) => (
                                        <li key={index}>
                                            商品ID: {item.inventory} / 数量: {item.quantity} / 単価: ¥{item.unit_price} / 小計: ¥{item.subtotal}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default TransactionHistoryDetail;
